<template>
  <LoadingSpinner v-if="projectLoading || invoicesLoading" />
  <div v-else class="list-invoices">
    <ProjectHeader :project="project" :hide-actions="true" />
    <ProjectSubNavigation v-if="isEmployee" :project="project" />

    <div class="work-orders mt-8 card">
      <div class="flex justify-between items-center">
        <h3 class="text-lg font-semibold">Invoices</h3>
        <BaseButton
          v-if="isEmployee"
          :to="{ name: 'CreateInvoice', query: { projectId, backTo: $route.fullPath } }"
          variant="base"
          icon-class="i-heroicons-plus"
        >
          Create Invoice
        </BaseButton>
      </div>

      <BaseTable
        v-if="invoices"
        :is-pending="invoicesLoading"
        :items="invoices"
        :fields="invoiceFields"
        :hide-header="false"
      >
        <template #invoiceId-data="{ row }">
          <RouterLink :to="{ name: 'ShowInvoice', params: { id: row.id } }">
            <span>{{ row.invoiceId }}</span>
          </RouterLink>
        </template>

        <template #status-data="{ row }">
          <BaseBadge :variant="row.status === INVOICE_STATUS.OVERDUE ? 'error' : row.status === INVOICE_STATUS.PAID ? 'success' : 'warning'">
            <span class="capitalize">{{ row.status }}</span>
          </BaseBadge>
        </template>

        <template #invoiceDate-data="{ row }">
          <span>{{ formatDate(row.invoiceDate) }}</span>
        </template>

        <template #total-data="{ row }">
          <span>{{ currency(row.total) }}</span>
        </template>
      </BaseTable>
    </div>
  </div>
</template>

<script lang="ts" setup>
import ProjectSubNavigation from '@/components/project/ProjectSubNavigation.vue'
import ProjectHeader from '@/components/project/ProjectHeader.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import ProjectService from '@/services/ProjectService'
import InvoiceService from '@/services/InvoiceService'
import { useQuery } from '@tanstack/vue-query'
import { useAuth } from '@/composables/useAuth'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { formatDate, currency } from '@/utils'
import { INVOICE_STATUS } from '@/models/invoice'

useHead({ title: 'Project Invoices' })

const $route = useRoute()
const { isEmployee } = useAuth()
const projectId = $route.params.projectId as string
const invoiceFields = [
  { key: 'invoiceId', label: 'Invoice #' },
  { key: 'status', label: 'Status' },
  { key: 'invoiceDate', label: 'Date' },
  { key: 'total', label: 'Total' },
]

const invoiceService = new InvoiceService()
const projectService = new ProjectService()

const { data: project, isLoading: projectLoading } = useQuery({
  queryKey: ['project', projectId],
  queryFn: () => projectService.find(projectId),
  enabled: !!projectId
})

const { data: invoices, isLoading: invoicesLoading } = useQuery({
  queryKey: ['invoices', projectId],
  queryFn: () => invoiceService.find_by('projectId', '==', projectId),
  enabled: !!projectId
})

</script>
